import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, message, Modal, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { useUserStore } from "@/views/stores";
import {
    getActivitySpecialProfile,
    type ActivityPageResult,
    type ActivityQuery,
    type ActivityRow,
    type ActivitySpecial
} from "./types";
import "./ActivityPage.scss";

// 沿用 Vue useTable 的商品活动列表默认分页大小。
const defaultQuery: ActivityQuery = { page: 1, size: 10 };
const activityStateLabels: Record<number, string> = { 0: "已关闭", 1: "已开启", 2: "草稿" };
const activityTypeLabels: Record<number, string> = { 1: "活动报名", 2: "活动商品" };
type ActivityFilterValues = ActivityQuery & { createdTime?: [string, string] | null };

/** 按旧 Vue 列表的 DATETIME_PICKER_FORMAT 展示活动时间字段。 */
const formatActivityDateTime = (value?: string) => (value ? dateFormat(value, DATETIME_PICKER_FORMAT) : "-");

/** 对齐 Vue showOpenBtn：只有已关闭且当前时间处于活动有效期内才允许开启。 */
function isActivityOpenable(row: ActivityRow): boolean {
    const start = row.campaign.validTimeStart ? Date.parse(row.campaign.validTimeStart) : Number.NaN;
    const end = row.campaign.validTimeFinish ? Date.parse(row.campaign.validTimeFinish) : Number.NaN;
    return Number.isFinite(start) && Number.isFinite(end) && start < Date.now() && end > Date.now();
}

export interface ActivityPageProps {
    assignActivity?: (id: number | string, handlerId: number | string) => Promise<void>;
    closeActivity?: (id: number | string) => Promise<void>;
    deleteActivity?: (id: number | string) => Promise<void>;
    loadActivities?: (query: ActivityQuery) => Promise<ActivityPageResult>;
    loadSpecials?: (keyword: string) => Promise<ActivitySpecial[]>;
    openActivity?: (id: number | string) => Promise<void>;
    permissions?: string[];
}

/** 复用 Vue 活动分页接口。 */
export async function loadActivities(query: ActivityQuery): Promise<ActivityPageResult> {
    const response = await request.service.get<ActivityPageResult>("/admin/campaign/paging", { params: query });
    return response.data;
}

/** 复用 Vue 草稿活动删除接口。 */
export async function deleteActivity(id: number | string): Promise<void> {
    await request.service.delete(`/admin/campaign/delete/${id}`);
}

/** 复用 Vue 活动开启接口。 */
export async function openActivity(id: number | string): Promise<void> {
    await request.service.put(`/admin/campaign/open/${id}`);
}

/** 复用 Vue 活动关闭接口。 */
export async function closeActivity(id: number | string): Promise<void> {
    await request.service.put(`/admin/campaign/close/${id}`);
}

/** 复用 Vue 活动专员查询接口。 */
export async function loadActivitySpecials(keyword: string): Promise<ActivitySpecial[]> {
    const response = await request.service.get<ActivitySpecial[]>(
        `/admin/campaign/find/specials?name=${encodeURIComponent(keyword)}`
    );
    return response.data;
}

/** 复用 Vue 活动专员指派接口。 */
export async function assignActivity(id: number | string, handlerId: number | string): Promise<void> {
    await request.service.get(`/admin/campaign/assign/${id}/${handlerId}`);
}

/** 将 Vue 活动分页返回的状态和内容配置枚举统一为 React 列表使用的数字。 */
function normalizeActivityRows(rows?: ActivityRow[]): ActivityRow[] {
    return (rows || []).map(row => ({
        ...row,
        campaign: {
            ...row.campaign,
            state: row.campaign.state == null ? undefined : Number(row.campaign.state),
            contentConfigs: row.campaign.contentConfigs?.map(config => Number(config))
        }
    }));
}

/** 商品活动列表，完整复用 Vue 列表字段、权限和草稿删除动作。 */
export function ActivityPage({
    assignActivity: assign = assignActivity,
    closeActivity: close = closeActivity,
    deleteActivity: remove = deleteActivity,
    loadActivities: load = loadActivities,
    loadSpecials: loadSpecialsForActivity = loadActivitySpecials,
    openActivity: open = openActivity,
    permissions
}: ActivityPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions || storePermissions;
    const canWrite = currentPermissions.includes("ROLE_ACTI_W");
    const [items, setItems] = useState<ActivityRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [query, setQuery] = useState<ActivityQuery>(defaultQuery);
    const [filterForm] = Form.useForm<ActivityFilterValues>();
    const [assignTarget, setAssignTarget] = useState<ActivityRow | null>(null);
    const [specials, setSpecials] = useState<ActivitySpecial[]>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState("");
    const [specialKeyword, setSpecialKeyword] = useState("");
    const [confirmation, setConfirmation] = useState<{
        id: number | string;
        action: "open" | "close" | "delete";
        title: string;
    }>();

    const refresh = async (targetQuery: ActivityQuery = query) => {
        const result = await load(targetQuery);
        setItems(normalizeActivityRows(result.list));
        setTotal(result.total || 0);
    };

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setItems(normalizeActivityRows(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("活动加载失败");
                    setItems([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    // 将 Vue 表格筛选条件转换为后端约定的分页查询参数。
    const submitFilters = (values: ActivityFilterValues) => {
        const { createdTime, ...rawFilters } = values;
        const [createdTimeStart, createdTimeEnd] = createdTime || [];
        if (createdTimeStart && createdTimeEnd && createdTimeEnd < createdTimeStart) {
            setErrorMessage("创建结束日期不能早于开始日期");
            return;
        }
        const filters = Object.fromEntries(
            Object.entries(rawFilters).filter(([, value]) => value !== undefined && value !== null && value !== "")
        ) as Omit<ActivityQuery, "page" | "size">;
        if (createdTimeStart) filters.startTime = `${createdTimeStart}T00:00:00`;
        if (createdTimeEnd) filters.endTime = `${createdTimeEnd}T23:59:59`;
        if (filters.state !== undefined) filters.state = Number(filters.state);
        setErrorMessage(null);
        setQuery(current => ({ page: 1, size: current.size, ...filters }));
    };

    const resetFilters = () => {
        filterForm.resetFields();
        setQuery(current => ({ page: 1, size: current.size }));
    };

    const handleDelete = async (id?: number | string) => {
        if (id == null) return;
        try {
            await remove(id);
            notifyOperationSuccess("已删除");
            await refresh();
        } catch {
            // 草稿活动删除失败时保留当前列表，并反馈 Vue 页面同类操作错误。
            setErrorMessage("活动删除失败");
        }
    };

    // 开启或关闭成功后重新加载活动分页，保持列表状态与服务端一致。
    const handleOpen = async (id?: number | string) => {
        if (id == null) return;
        try {
            await open(id);
            notifyOperationSuccess("已开启");
            await refresh();
        } catch {
            setErrorMessage("活动开启失败");
        }
    };

    const handleClose = async (id?: number | string) => {
        if (id == null) return;
        try {
            await close(id);
            notifyOperationSuccess("已关闭");
            await refresh();
        } catch {
            setErrorMessage("活动关闭失败");
        }
    };

    const showAssign = async (row: ActivityRow) => {
        if (row.campaign.id == null) return;
        try {
            setSpecialKeyword("");
            setSpecials(await loadSpecialsForActivity(""));
            setSelectedHandlerId(row.campaign.handlerId == null ? "" : String(row.campaign.handlerId));
            setAssignTarget(row);
        } catch {
            setErrorMessage("活动专员加载失败");
        }
    };

    const searchSpecials = async () => {
        try {
            setSpecials(await loadSpecialsForActivity(specialKeyword));
        } catch {
            setErrorMessage("活动专员加载失败");
        }
    };

    /** 复用旧 Vue 活动编号复制交互，复制失败时只提示轻量错误，不改变列表数据。 */
    const copyActivityCode = async (code?: string) => {
        if (!code) return;
        try {
            if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
            await navigator.clipboard.writeText(code);
            notifyOperationSuccess("已复制");
        } catch {
            message.error("复制失败");
        }
    };

    const submitAssign = async () => {
        if (assignTarget?.campaign.id == null || !selectedHandlerId) return;
        try {
            await assign(assignTarget.campaign.id, Number(selectedHandlerId));
            notifyOperationSuccess("已指派");
            setAssignTarget(null);
            await refresh();
        } catch {
            setErrorMessage("活动指派失败");
        }
    };

    /** 旧 Vue 通过 confirmable 装饰器在真正请求前展示确认层。 */
    const submitConfirmedAction = async () => {
        if (!confirmation) return;
        const current = confirmation;
        setConfirmation(undefined);
        if (current.action === "open") await handleOpen(current.id);
        else if (current.action === "close") await handleClose(current.id);
        else await handleDelete(current.id);
    };

    const getSpecialId = (special: ActivitySpecial) => getActivitySpecialProfile(special).id;

    // 将 Vue 活动列表的更新时间排序转换为活动分页接口字段，并回到第一页。
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(current => {
            const nextQuery = { ...current, page: 1 };
            delete nextQuery.sortKey;
            delete nextQuery.sortOrder;
            if (prop === "campaign.updatedTime" && order) {
                nextQuery.sortKey = "updated_time";
                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
            } else if (prop === "campaign.createdTime" && order) {
                nextQuery.sortKey = "created_time";
                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
            }
            return nextQuery;
        });
    };

    // 用 zhihao-ui-react 列契约承接 Vue 活动列表的字段、状态和专员操作。
    const columns: TableColumnProps<ActivityRow>[] = [
        {
            key: "code",
            label: "活动编号",
            prop: "campaign.code",
            width: 160,
            render: ({ row }) => (
                <Space size={2}>
                    <Button
                        aria-label="查看活动详情"
                        type="link"
                        onClick={() =>
                            void navigate(
                                `/productActivity/activity/detail?id=${row.campaign.code || ""}&campaignId=${row.campaign.id || ""}`
                            )
                        }
                    >
                        {row.campaign.code || "-"}
                    </Button>
                    {row.campaign.code ? (
                        <Button
                            aria-label="复制活动编号"
                            autoInsertSpace={false}
                            className="activity-code-copy-button"
                            size="small"
                            type="link"
                            onClick={() => void copyActivityCode(row.campaign.code)}
                        >
                            <span aria-hidden="true" className="iconfont icon-a-Frame28" />
                        </Button>
                    ) : null}
                </Space>
            )
        },
        {
            key: "title",
            label: "活动标题",
            prop: "campaign.title",
            minWidth: 300,
            render: ({ row }) => row.campaign.title || "-"
        },
        {
            key: "validTime",
            label: "活动时间范围",
            prop: "validTimeStart",
            minWidth: 260,
            render: ({ row }) => (
                <span className="activity-list-time-cell">
                    {row.campaign.validTimeStart && row.campaign.validTimeFinish
                        ? `${formatActivityDateTime(row.campaign.validTimeStart)}至${formatActivityDateTime(row.campaign.validTimeFinish)}`
                        : "-"}
                </span>
            )
        },
        {
            key: "quantity",
            label: "商品数量(个)",
            prop: "quantity",
            minWidth: 120,
            render: ({ row }) => row.quantity ?? "-"
        },
        {
            key: "contentConfigs",
            label: "内容配置",
            prop: "contentConfigs",
            minWidth: 160,
            render: ({ row }) =>
                row.campaign.contentConfigs?.map(value => activityTypeLabels[value] || value).join("、") || "-"
        },
        {
            key: "handlerName",
            label: "活动专员",
            prop: "campaign.handlerName",
            minWidth: 120,
            render: ({ row }) => row.campaign.handlerName || "-"
        },
        {
            key: "state",
            label: "活动状态",
            prop: "campaign.state",
            minWidth: 120,
            render: ({ row }) => (
                <span className="activity-state-label">{activityStateLabels[row.campaign.state ?? -1] || "-"}</span>
            )
        },
        {
            key: "updatedTime",
            label: "更新时间",
            prop: "campaign.updatedTime",
            sortable: "custom",
            minWidth: 188,
            render: ({ row }) => formatActivityDateTime(row.campaign.updatedTime)
        },
        {
            key: "createdTime",
            label: "创建时间",
            prop: "campaign.createdTime",
            sortable: "custom",
            minWidth: 188,
            render: ({ row }) => formatActivityDateTime(row.campaign.createdTime)
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            // 操作项随排序结果变化，但列宽固定取三项常见动作的空间，避免表格横向跳动。
            // Vue 固定右侧操作列按三项动作的实际内容宽度展开，避免末项按钮被固定列裁切。
            width: 120,
            fixed: "right",
            className: "admin-table-operation-column compact",
            labelClassName: "admin-table-operation-column compact",
            showOverflowTooltip: false,
            render: ({ row }) => {
                if (!canWrite) return null;
                return (
                    <Space>
                        <Button
                            aria-label={`变更指派活动${row.campaign.id || ""}`}
                            size="small"
                            type="link"
                            onClick={() => void showAssign(row)}
                        >
                            变更指派
                        </Button>
                        <Button
                            aria-label="编辑活动"
                            size="small"
                            type="link"
                            onClick={() =>
                                void navigate(
                                    `/productActivity/activity/edit?id=${row.campaign.code || ""}&campaignId=${row.campaign.id || ""}`
                                )
                            }
                        >
                            编辑
                        </Button>
                        {row.campaign.state === 1 ? (
                            <Button
                                aria-label={`关闭活动${row.campaign.id || ""}`}
                                size="small"
                                type="link"
                                onClick={() =>
                                    row.campaign.id != null &&
                                    setConfirmation({ id: row.campaign.id, action: "close", title: "是否关闭活动" })
                                }
                            >
                                关闭
                            </Button>
                        ) : null}
                        {row.campaign.state === 0 && isActivityOpenable(row) ? (
                            <Button
                                aria-label={`开启活动${row.campaign.id || ""}`}
                                size="small"
                                type="link"
                                onClick={() =>
                                    row.campaign.id != null &&
                                    setConfirmation({ id: row.campaign.id, action: "open", title: "是否开启活动" })
                                }
                            >
                                开启
                            </Button>
                        ) : null}
                        {row.campaign.state === 2 ? (
                            <Button
                                aria-label="删除活动"
                                danger
                                size="small"
                                type="link"
                                onClick={() =>
                                    row.campaign.id != null &&
                                    setConfirmation({ id: row.campaign.id, action: "delete", title: "是否删除活动" })
                                }
                            >
                                删除
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="activity-page" className="activity-page flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<ActivityRow>
                    data={items}
                    // 保持 Vue 列表的固定操作列宽，排序时不因当前行动作数量变化而横向跳动。
                    columns={normalizeOperationColumns(columns, [])}
                    onSortChange={handleSortChange}
                    pagination={false}
                    rowKey={row => String(row.campaign.id || row.campaign.code)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    showOverflowTooltip
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                活动列表
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form activity-page__filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                                onValuesChange={(changedValues, values) => {
                                    // Vue SearchForm 清空字段时立即查询；普通输入仍由“搜索”按钮提交。
                                    const cleared = Object.values(changedValues).some(
                                        value =>
                                            value === undefined ||
                                            value === null ||
                                            value === "" ||
                                            (Array.isArray(value) && value.length === 0)
                                    );
                                    if (cleared) submitFilters(values);
                                }}
                            >
                                {canWrite ? (
                                    <Button
                                        className="admin-table-primary-action"
                                        style={{ width: "auto" }}
                                        type="primary"
                                        onClick={() => void navigate("/productActivity/activity/add")}
                                    >
                                        新增活动
                                    </Button>
                                ) : null}
                                <Form.Item className="activity-filter-code" label="活动编号" name="code">
                                    <Input allowClear placeholder="请输入活动编号搜索" />
                                </Form.Item>
                                <Form.Item className="activity-filter-title" label="活动标题" name="title">
                                    <Input allowClear placeholder="请输入活动标题搜索" />
                                </Form.Item>
                                <Form.Item className="activity-filter-handler" label="活动专员" name="handlerName">
                                    <Input allowClear placeholder="请输入活动专员" />
                                </Form.Item>
                                <Form.Item label="活动状态" name="state">
                                    <Select
                                        allowClear
                                        aria-label="活动状态"
                                        className="activity-filter-state"
                                        options={Object.entries(activityStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        classNames={{ popup: { root: "activity-state-dropdown" } }}
                                        placeholder="请选择活动状态"
                                    />
                                </Form.Item>
                                <Form.Item label="创建时间" name="createdTime">
                                    <ZhDateRangePicker
                                        format="YYYY-MM-DD"
                                        rangeSeparator="-"
                                        startPlaceholder="创建开始日期"
                                        endPlaceholder="创建结束日期"
                                        type="daterange"
                                        valueFormat="YYYY-MM-DD"
                                    />
                                </Form.Item>
                                <Button
                                    aria-label="查询活动"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={resetFilters}
                                />
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                // 沿用 Vue 分页参数，切页保留筛选条件；切换页大小时回到第 1 页。
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                open={Boolean(assignTarget)}
                modalClass="assign-dialog-modal"
                title="指派专员"
                width="842px"
                onCancel={() => setAssignTarget(null)}
                footer={
                    <Space>
                        <Button onClick={() => setAssignTarget(null)}>取消</Button>
                        <Button disabled={!selectedHandlerId} onClick={() => void submitAssign()} type="primary">
                            确认
                        </Button>
                    </Space>
                }
            >
                <div className="assign-dialog-body">
                    <div className="assign-dialog-search-header">
                        <Input
                            aria-label="专员姓名"
                            className="assign-dialog-search"
                            placeholder="请输入姓名搜索"
                            onPressEnter={() => void searchSpecials()}
                            value={specialKeyword}
                            onChange={event => setSpecialKeyword(event.target.value)}
                        />
                        <Button
                            aria-label="搜索专员"
                            className="assign-dialog-search-button"
                            onClick={() => void searchSpecials()}
                        >
                            <TableSearchIcon name="search" />
                        </Button>
                    </div>
                    {specials.length ? (
                        <div className="assign-dialog-list">
                            {specials.map(special => {
                                const profile = getActivitySpecialProfile(special);
                                const specialId = getSpecialId(special);
                                const selected = specialId != null && String(specialId) === selectedHandlerId;
                                return (
                                    <button
                                        aria-label={`选择${profile.name || "活动专员"}`}
                                        aria-pressed={selected}
                                        className={`assign-dialog-card${selected ? " is-selected" : ""}`}
                                        key={String(specialId)}
                                        type="button"
                                        onClick={() => specialId != null && setSelectedHandlerId(String(specialId))}
                                    >
                                        <div className="assign-dialog-card-info">
                                            <div className="assign-dialog-card-name">
                                                {profile.name || "-"}{" "}
                                                <span className="assign-dialog-employee-id">
                                                    {profile.employeeId || ""}
                                                </span>
                                            </div>
                                            <div className="assign-dialog-follow-up">
                                                跟进的活动数量 <b>{special.productCount ?? ""}</b> 条
                                            </div>
                                        </div>
                                        <span
                                            aria-hidden="true"
                                            className="assign-dialog-card-radio"
                                            data-selected={String(selected)}
                                        >
                                            {selected ? <span className="assign-dialog-card-radio-dot" /> : null}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="py-12 text-center text-gray-400">暂未设定可指派的服务人员</div>
                    )}
                </div>
            </ZhDialog>
            <Modal
                cancelText="取消"
                okText="确认"
                open={Boolean(confirmation)}
                title={confirmation?.title}
                onCancel={() => setConfirmation(undefined)}
                onOk={() => void submitConfirmedAction()}
            />
        </section>
    );
}

export default ActivityPage;
